import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getItemsByGenre, getItemById as fetchItemById } from '../api/itemsApi';
import { normalizeProgram } from '../models/Program';

const ItemsContext = createContext(null);

export const ItemsProvider = ({ children }) => {
  const [itemsByGenre, setItemsByGenre] = useState({});
  const [loadingByGenre, setLoadingByGenre] = useState({});
  const [errorByGenre, setErrorByGenre] = useState({});

  // Keep latest state in refs so callbacks can be stable (prevents useEffect loops)
  const itemsByGenreRef = useRef(itemsByGenre);
  const loadingByGenreRef = useRef(loadingByGenre);
  useEffect(() => {
    itemsByGenreRef.current = itemsByGenre;
  }, [itemsByGenre]);
  useEffect(() => {
    loadingByGenreRef.current = loadingByGenre;
  }, [loadingByGenre]);

  const loadItems = useCallback(async (genre, { force = false } = {}) => {
    if (!genre) return;

    // If already loaded (even empty array), don't refetch unless forced
    if (!force && itemsByGenreRef.current[genre]) return;

    // Prevent duplicate in-flight requests for the same genre (stops "retry forever" loops)
    if (loadingByGenreRef.current[genre]) return;

    setLoadingByGenre((s) => ({ ...s, [genre]: true }));
    setErrorByGenre((s) => ({ ...s, [genre]: null }));
    try {
      const data = await getItemsByGenre(genre);
      // normalize items into Program model and filter by genre
      const normalized = Array.isArray(data)
        ? data.map(normalizeProgram).filter(p => String(p.genre) === String(genre))
        : [];
      setItemsByGenre((s) => ({ ...s, [genre]: normalized }));
    } catch (err) {
      setErrorByGenre((s) => ({ ...s, [genre]: err.message || String(err) }));
    } finally {
      setLoadingByGenre((s) => ({ ...s, [genre]: false }));
    }
  }, []);

  const refreshItems = useCallback(async (genre) => {
    if (!genre) return;
    setItemsByGenre((s) => ({ ...s, [genre]: undefined }));
    return loadItems(genre, { force: true });
  }, [loadItems]);

  const getItemById = useCallback(async (id) => {
    if (!id) return null;
    // Search through all genres for the item
    for (const genre in itemsByGenreRef.current) {
      const items = itemsByGenreRef.current[genre];
      if (Array.isArray(items)) {
        const item = items.find(item => String(item.id) === String(id));
        if (item) return item;
      }
    }
    // If not found in cache, fetch from API
    try {
      const data = await fetchItemById(id);
      console.log('Fetched item from API:', data);
      if (data) {
        const normalized = normalizeProgram(data);
        console.log('Normalized item:', normalized);
        return normalized;
      }
    } catch (err) {
      console.error('Error fetching item by id:', err);
    }
    return null;
  }, []);

  return (
    <ItemsContext.Provider value={{ itemsByGenre, loadItems, refreshItems, getItemById, loadingByGenre, errorByGenre }}>
      {children}
    </ItemsContext.Provider>
  );
};

export const useItems = () => {
  const ctx = useContext(ItemsContext);
  if (!ctx) throw new Error('useItems must be used within ItemsProvider');
  return ctx;
};

export default ItemsContext;
